import { useEffect } from "react";
import zoomSdk from "@zoom/appssdk";
import {
  AIMS_STATUS,
  ROLES,
  SUMMARIZE_STATES,
  TRANSCRIPTION_STATES,
  AVAILABILITY_TIMEOUT_ERROR,
} from "../constants";
import LogManager from "../utils/logManager";
import { fetchAimsAvailabilityApi } from "../utils/aimsAvailabilityUtils";
import { fetchRtmsSessionStatus, SESSION_STATUS } from "../utils/rtmsSessionStatusUtils";
import { configureZoomSdk } from "../utils/zoomSdkUtils";
import { isLocalhost } from "../utils/environmentUtils";
import { AimsAvailabilityApiResponse, sampleActionItems, sampleAgendaItems } from "../localtest/LocalhostTest";
import {
  clearSummariesCache,
  handleAimsAvailabilityResult,
  resolveRtmsStartedByOther,
  canAutoStartTranscription,
} from "../utils/meetingInitUtils";

const SHAREDEV_HOSTNAME = "sharedevaimsui.test.aims.meetex.enterprise-engineering.aws.dev";

/**
 * Configure the Zoom SDK and fetch meeting/user context once on mount, applying
 * the results through the provided state setters. On localhost it seeds mock
 * state instead. Owns the initialization effect that previously lived in Home.
 *
 * @param {object} callbacks - State setters, refs, loadPreferences, and insightStore.
 */
export const useInMeetingInitialization = (callbacks) => {
  useEffect(() => {
    const {
      setRunningContext,
      setLastKnownContext,
      setMeetingUUID,
      setMeetingId,
      setRole,
      setAlias,
      setAimsStatus,
      setAimsNotAvailableMessage,
      setHasGermanyAttendee,
      setAutoStartMode,
      setAppContext,
      setSummarizeState,
      setTranscriptionState,
      setIsRtmsStartedByOther,
      setRtmsRequesterName,
      setRtmsStreamId,
      setIsAutostarting,
      setIsLoading,
      rtmsStartTimestampRef,
      loadPreferences,
      setInviteeAliases,
      setExternalInvitees,
      insightStore,
    } = callbacks;

    LogManager.metric("Home_PageView", 1);

    // On localhost there is no Zoom SDK or backend, so seed mock state that
    // mirrors the real init's state shape and bail out.
    if (isLocalhost()) {
      console.log("[Localhost] Running in localhost mode.");
      const availabilityResult = handleAimsAvailabilityResult(AimsAvailabilityApiResponse);
      setRole(availabilityResult.role);
      setAlias(availabilityResult.alias);
      // Override for testing - change these to test different views
      setRole(ROLES.ORGANIZER);
      setRunningContext("inMeeting");
      setMeetingUUID("testUUID");
      setAimsStatus(AIMS_STATUS.AVAILABLE);
      setSummarizeState(SUMMARIZE_STATES.SUMMARIZING);
      setRtmsRequesterName("John Doe");
      setRtmsStreamId("test-stream-id");
      setIsRtmsStartedByOther(false);
      setIsLoading(false);
      // Seed sample action items and agenda items so those tabs show populated cards
      // locally (no backend on localhost). The seed effects/polls are already skipped
      // for localhost (useActionItemsSeed/useAgendaSeed bail on a localhost URL), so
      // these upserts are authoritative.
      insightStore.upsertActionItems(sampleActionItems);
      insightStore.upsertAgendaItems(sampleAgendaItems);
      loadPreferences();
      return;
    }

    // Configure the Zoom SDK and read the running/meeting/user context. Returns
    // { isMainClient: true } when running in the main client (nothing more to do),
    // otherwise { isMainClient: false, meetingUUID, userContext }.
    const loadZoomContext = async () => {
      await configureZoomSdk();

      const runningContext = await zoomSdk.getRunningContext();
      LogManager.info("The runningContext is:", runningContext);

      console.log("Document Visibility State:", document.visibilityState);
      setRunningContext(runningContext.context);
      setLastKnownContext(runningContext.context);

      if (runningContext.context === "inMainClient") {
        LogManager.info("The app is running in main client now, show MainClientHome view.");
        return { isMainClient: true };
      }

      // The runningContext is inMeeting
      const meetingUUID = (await zoomSdk.getMeetingUUID()).meetingUUID;
      console.log("The meetingUUID is:" + meetingUUID);
      setMeetingUUID(meetingUUID);

      const userContext = await zoomSdk.getUserContext();
      LogManager.info("The userContext is:", userContext);

      return { isMainClient: false, meetingUUID, userContext };
    };

    // Fetch AIMS availability for hosts/coHosts and apply the results. Attendees
    // have no availability call. Returns the aimsAvailability response (or null).
    const loadHostAvailability = async (meetingUUID, userContext) => {
      if (!["host", "coHost"].includes(userContext.role)) {
        LogManager.info("The app is running inMeeting and user is attendee.");
        setRole(ROLES.ATTENDEE);
        loadPreferences();
        return null;
      }

      LogManager.info("The app is running inMeeting and user is host or coHost.");

      // Only host and coHost can call this api
      const meetingContext = await zoomSdk.getMeetingContext();
      LogManager.info("The meetingContext is: ", meetingContext);
      setMeetingId(meetingContext.meetingID);

      const [aimsAvailability] = await Promise.all([
        fetchAimsAvailabilityApi(
          meetingUUID,
          meetingContext.meetingID,
          meetingContext.meetingTopic,
          userContext.screenName
        ),
        loadPreferences(),
      ]);

      const availabilityResult = handleAimsAvailabilityResult(aimsAvailability);
      setRole(availabilityResult.role);
      setAlias(availabilityResult.alias);
      setHasGermanyAttendee(availabilityResult.hasGermanyAttendee);
      setAimsStatus(availabilityResult.aimsStatus);
      setAutoStartMode(availabilityResult.autoStartMode);
      if (availabilityResult.inviteeAliases) {
        setInviteeAliases(availabilityResult.inviteeAliases);
      }
      if (availabilityResult.externalInvitees) {
        setExternalInvitees(availabilityResult.externalInvitees);
      }
      if (availabilityResult.aimsNotAvailableMessage) {
        setAimsNotAvailableMessage(availabilityResult.aimsNotAvailableMessage);
      }

      // Get app context for secure backend validation (only needed for organizers/alternative hosts)
      try {
        const appContextResponse = await zoomSdk.getAppContext();
        if (window.location.hostname === SHAREDEV_HOSTNAME) {
          // Temporary AiMS for China POC instrumentation. This intentionally exposes the
          // complete, replayable Zoom app context only in the local ShareDev Web Inspector.
          // Do not replace console with LogManager because that would persist it remotely.
          console.log("[AiMS China POC] getAppContext response:", appContextResponse);
          console.log(
            "[AiMS China POC] getAppContext JSON snapshot:",
            JSON.stringify(appContextResponse)
          );
          console.log(
            "[AiMS China POC] raw app context:",
            appContextResponse?.context
          );
        }
        LogManager.metric("ZoomSDK_GetAppContext_Success", 1);
        // Log only that a context arrived, NEVER its value (Requirement 3.9) — see
        // the note in pages/Home.jsx for why.
        LogManager.info(
          "app context received: " +
            (appContextResponse?.context ? "present" : "absent")
        );

        if (!appContextResponse || !appContextResponse.context) {
          LogManager.error("getAppContext() returned invalid response - cannot proceed with RTMS operations");
          setAimsStatus(AIMS_STATUS.EXCEPTION);
        } else {
          setAppContext(appContextResponse.context);
        }
      } catch (err) {
        LogManager.error("Failed to get app context from Zoom SDK:", err);
        LogManager.metric("ZoomSDK_GetAppContext_Failure", 1);
        setAimsStatus(AIMS_STATUS.EXCEPTION);
      }

      LogManager.info("Finish the initialization for host or coHost.");
      return aimsAvailability;
    };

    // Fetch the RTMS session status (all user types) and apply the results.
    // Returns whether there is an active session.
    const loadRtmsSessionStatus = async (userContext, aimsAvailability, meetingUUID) => {
      const sessionStatus = await fetchRtmsSessionStatus(meetingUUID);
      LogManager.info("RTMS status is", sessionStatus);

      const hasActiveSession = sessionStatus.status === SESSION_STATUS.ACTIVE;

      if (hasActiveSession) {
        setSummarizeState(SUMMARIZE_STATES.SUMMARIZING);
        setTranscriptionState(TRANSCRIPTION_STATES.TRANSCRIBING);
        rtmsStartTimestampRef.current = sessionStatus.rtmsStartTime;

        if (sessionStatus.rtmsStreamId) {
          setRtmsStreamId(sessionStatus.rtmsStreamId);
          LogManager.info("Saved rtmsStreamId on initialization:", sessionStatus.rtmsStreamId);
        }

        // Determine who started the session
        const { isStartedByOther, requesterName } = resolveRtmsStartedByOther({
          userContext,
          aimsAvailability,
          sessionStatus,
        });
        setIsRtmsStartedByOther(isStartedByOther);
        if (isStartedByOther) {
          setRtmsRequesterName(requesterName);
        }
      } else {
        LogManager.info("No RTMS session found");
        setSummarizeState(SUMMARIZE_STATES.DEFAULT);
        setTranscriptionState(TRANSCRIPTION_STATES.DEFAULT);
      }

      return hasActiveSession;
    };

    // Transcription auto-start (single-stream needs no active session; per-host multi always applies)
    const autoStartTranscription = (aimsAvailability, hasActiveSession) => {
      if (canAutoStartTranscription({ aimsAvailability, hasActiveSession })) {
        LogManager.info(
          `Autostart conditions met for transcription: transcript available, enableAutoStart is true, ` +
            `autoStartMode is ${aimsAvailability.autoStartMode}, hasActiveSession is ${hasActiveSession}`
        );
        setIsAutostarting(true);
      }
    };

    // Orchestrate the meeting initialization sequence, applying results through
    // the provided state setters.
    const runInitialization = async () => {
      clearSummariesCache();

      try {
        const zoomContext = await loadZoomContext();
        if (zoomContext.isMainClient) {
          return;
        }

        const { meetingUUID, userContext } = zoomContext;
        const aimsAvailability = await loadHostAvailability(meetingUUID, userContext);
        const hasActiveSession = await loadRtmsSessionStatus(userContext, aimsAvailability, meetingUUID);
        autoStartTranscription(aimsAvailability, hasActiveSession);
      } catch (error) {
        LogManager.error("Error fetching context or call AimsAvailability/RTMS Status API:", error);
        setAimsStatus(error.message === AVAILABILITY_TIMEOUT_ERROR ? AIMS_STATUS.TIMEOUT : AIMS_STATUS.EXCEPTION);
      } finally {
        setIsLoading(false);
      }
    };

    runInitialization();
    // Run once on mount, mirroring the original Home effect. callbacks is rebuilt
    // each render, so it is intentionally excluded from the dependency array.
  }, []);
};
